<script setup lang="ts">
import type { EventListItemDTO } from '~/shared/api/client/events/events.dto';
import DateBadge from '~/shared/ui/date-badge/DateBadge.vue';
import { getEventCardStub } from '~/shared/lib/getEventStubImage';
import { getEventRoute, isPastEvent } from '~/shared/lib/getEventRoute';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { EVENT_CARD_IMAGE_WIDTHS } from '~/shared/lib/events-images.const';

const props = defineProps<{
  event: EventListItemDTO;
  place?: string;
  imageUrl?: string | null;
}>();

const displayImage = computed(
  () => props.imageUrl || getEventCardStub(props.event.id),
);

const isPast = computed(() => isPastEvent(props.event));

const linkTo = computed(() => getEventRoute(props.event));

/*
  The box ratio changes between breakpoints, so only scale down: a server
  crop would be cut again by object-fit in a narrower box.
*/
const imageSrc = computed(() =>
  resizeImage(displayImage.value, { width: 320 }),
);

const imageSrcset = computed(() =>
  resizeImageSrcsetWidths(displayImage.value, EVENT_CARD_IMAGE_WIDTHS),
);
</script>

<template>
  <NuxtLink :to="linkTo" :class="$style.card">
    <div :class="$style.top">
      <div :class="$style.header">
        <DateBadge
          :start-time="event.start_time"
          :end-time="event.end_time"
          :month-only="event.month_only"
          variant="event-card"
        />
        <span v-if="place" :class="$style.place">{{ place }}</span>
      </div>
      <div :class="$style.title">
        {{ event.name }}
      </div>
    </div>
    <div :class="$style.media">
      <div :class="$style.photo">
        <img
          :src="imageSrc"
          :srcset="imageSrcset || undefined"
          sizes="(max-width: 479px) 100vw, (max-width: 767px) 260px, (max-width: 1023px) 215px, (max-width: 1379px) 200px, 320px"
          :alt="event.name"
          :class="$style.image"
        />
        <div :class="$style.overlay">
          <span :class="$style.button">{{
            isPast ? 'Как это было' : 'Принять участие'
          }}</span>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style module lang="scss">
.card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 365px;
  padding: 0 var(--space-2-5x) var(--space-2-5x);
  text-decoration: none;
  color: var(--color-black-1);
}

.top {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5x);
  width: 100%;
}

.header {
  display: flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
}

.place {
  @include text_caption2_caps;
  @include line_clamp(2);
  color: var(--color-black-2);
  flex: 1;
  min-width: 0;
  height: 30px;
}

.title {
  @include text_h5;
  @include line_clamp(10);
}

.media {
  display: flex;
  align-items: flex-end;
  flex: 1 1 0;
  min-height: 0;
  container-type: size;
}

@media (min-width: 1380px) {
  @container (max-height: 158px) {
    .photo {
      display: none;
    }
  }
}

.photo {
  position: relative;
  width: 100%;
  height: 159px;
  overflow: hidden;
  flex-shrink: 0;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.button {
  @include text_button3;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 7px 20px;
  background: var(--color-white);
  border: 1px solid var(--color-gray-line);
  color: var(--color-black-1);
  white-space: nowrap;
}

@media (hover: hover) {
  .card:hover .overlay {
    opacity: 1;
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 1379px) and (min-width: 480px) {
  .card {
    height: 288px;
    padding: var(--space-2-5x);
  }

  .header {
    align-items: flex-start;
  }

  .place {
    font-size: 9px;
    letter-spacing: 0.9px;
    line-height: 1.2;
    height: auto;
    padding-top: 10px;
  }

  .title {
    @include text_h5_mobile;
    @include line_clamp(8);
  }

  .photo {
    height: 108px;
  }

  @container (max-height: 107px) {
    .photo {
      display: none;
    }
  }
}

@media (max-width: 479px) {
  .card {
    height: auto;
    padding: var(--space-2-5x);
    gap: var(--space-2-5x);
    justify-content: flex-start;
  }

  .header {
    align-items: flex-start;
  }

  .place {
    font-size: 10px;
    letter-spacing: 1px;
    line-height: 1.2;
    height: auto;
    padding-top: 10px;
  }

  .title {
    @include line_clamp(4);
    font-size: 16px;
    line-height: 1.3;
  }

  .media {
    flex: none;
    container-type: normal;
  }

  .photo {
    height: 157px;
  }
}

// Fallback for browsers without container queries
@supports not (container-type: size) {
  .title {
    @include line_clamp(3);
  }

  .media {
    flex: none;
  }
}
</style>
